import React from 'react'

import './expandable-table-row.scss'

import cn from '../../../../commons/src/libs/class-names'
import {
  countChildrenWithFragments,
  mapChildrenWithFragments,
} from '../../../../commons/src/services/react-children'

import {CommonProps, Overwrite} from '../../../../commons/src/types'

import TableCell from './TableCell'
import TableRow from './TableRow'
import TableRowToggleButton from './TableRowToggleButton'

type Props = Overwrite<
  CommonProps<'div'>,
  {
    children: (params: {expanded: boolean; expansionToggle: React.ReactElement}) => React.ReactNode
    expanded: boolean
    toggle: () => void
    subRows: React.ReactNode
    computedWidths?: string[]
    aligns?: ('left' | 'center' | 'right')[]
    onClick?: () => void
  }
>

const ExpandableTableRow = ({
  className,
  children,
  expanded,
  toggle,
  subRows,
  computedWidths = [],
  aligns = [],
  onClick,
  ...props
}: Props) => {
  const expansionToggle = () => (
    <TableRowToggleButton
      expanded={expanded}
      onClick={event => {
        event.stopPropagation()
        toggle()
      }}
    />
  )

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (onClick) {
      const target = event.target as Element
      // only handle clicks on free space within the row, so users can for example copy content by double click
      if (target.matches('.TableCell') || target.matches('.TableCell__content')) {
        onClick()
      }
    }
  }

  const childElements = children({expanded: !!expanded, expansionToggle: expansionToggle()})
  const childCount = countChildrenWithFragments(childElements)

  return (
    <div
      {...props}
      className={cn('ExpandableTableRow', {clickable: !!onClick}, className)}
      onClick={handleClick}
    >
      <div className="ExpandableTableRow__content">
        {mapChildrenWithFragments<React.ComponentProps<typeof TableCell>>(
          childElements,
          (child, i) =>
            React.cloneElement(child, {
              computedWidth: computedWidths[i],
              align: aligns[i],
              firstCell: i === 0,
              lastCell: i === childCount - 1,
            })
        )}
      </div>
      {expanded && (
        <div className="TableRow__subs">
          {mapChildrenWithFragments<React.ComponentProps<typeof TableRow | typeof TableCell>>(
            subRows,
            (child, i) =>
              React.cloneElement(child, {
                computedWidths,
                aligns,
                key: i,
                subRow: true,
              })
          )}
        </div>
      )}
    </div>
  )
}

export default ExpandableTableRow
